/**
 * Visualization shapes for Deckspace.
 *
 * Chart type constants and unions, the discriminated `ChartMapping` union,
 * and data-view role shapes.
 *
 * Pure types and runtime constants — no class definitions.
 */

import type { ColorIntent } from './tokens.js';

/** Analytical charts — profiler + inference + column mapping */
export const CHART_TYPES = [
  'bar',
  'line',
  'area',
  'pie',
  'scatter',
  'heatmap',
  'histogram',
  'radar',
  'sankey',
  'funnel',
  'gauge',
] as const;
export type ChartType = (typeof CHART_TYPES)[number];

/** Interactive structured data views — semantic column roles, potential writeback */
export const DATA_VIEW_TYPES = ['table', 'kanban'] as const;
export type DataViewType = (typeof DATA_VIEW_TYPES)[number];

/** Structural/relational — code or graph input */
export const DIAGRAM_TYPES = ['mermaid', 'tree'] as const;
export type DiagramType = (typeof DIAGRAM_TYPES)[number];

/** Component types that require a sourceDataset and fetch data at render time. */
export const DATA_BACKED_TYPES = new Set([
  'Chart',
  'DataTable',
  'Kanban',
  'Sparkline',
  'List',
  'Calendar',
  'Timeline',
]);

/**
 * Tree node for hierarchical tree diagrams
 */
export interface TreeNode {
  label: string;
  children?: TreeNode[];
}

/**
 * Mermaid design preview styling
 * When present on a Mermaid mapping, enables design preview mode:
 * - User-specified colors/fonts instead of theme CSS variables
 * - Ignores light/dark mode changes
 * - Skips auto-legibility post-processing
 */
export interface MermaidStyle {
  /** Container background color */
  background?: string;
  /** Node colors for primary/secondary/tertiary nodes */
  nodeColors?: {
    primary?: string;
    primaryText?: string;
    primaryBorder?: string;
    secondary?: string;
    secondaryText?: string;
    secondaryBorder?: string;
    tertiary?: string;
    tertiaryText?: string;
    tertiaryBorder?: string;
  };
  /** Font family for all text */
  fontFamily?: string;
  /** Edge/arrow color */
  lineColor?: string;
  /** Edge label background color */
  labelBackground?: string;
}

/**
 * Annotation attached to a visualization
 */
export interface Annotation {
  id: string;
  text: string;
  author?: 'human' | 'ai';
  created: string; // ISO timestamp
}

export interface VisualizationConfig {
  title?: string;
  subtitle?: string;
  timezone?: string;
  intent?: ColorIntent;
}

/**
 * Display config for visualizations (title, formatting, intent)
 */
export interface VisualizationDisplayConfig {
  title?: string;
  subtitle?: string;
  timezone?: string;
  intent?: ColorIntent;
  format?: Record<string, string>; // column-level formatting
}

/** Alias — new config interfaces use this name. Original kept for backward compat. */
export type DisplayConfig = VisualizationDisplayConfig;

/**
 * Per-visualization mapping shapes. Each interface carries only the column-binding
 * fields used by its visualization type — no shared `kind` discriminator.
 *
 * Dispatch happens at the component level via `chartType` (on `Chart` ComponentNode props),
 * or via the ComponentNode `type` field for non-chart visualizations (Mermaid, Kanban,
 * DataTable). See `MappingFor<T>` below for `chartType`-keyed narrowing.
 */
export interface BarMapping {
  x: string;
  y: string | string[];
  groupBy?: string;
}
export interface LineMapping {
  x: string;
  y: string | string[];
  groupBy?: string;
}
export interface AreaMapping {
  x: string;
  y: string | string[];
  groupBy?: string;
}
export interface PieMapping {
  labels: string;
  values: string;
}
export interface ScatterMapping {
  xValues: string;
  yValues: string;
}
export interface HeatmapMapping {
  xLabels: string;
  yLabels: string;
  value: string;
}
export interface HistogramMapping {
  values: string;
  labels?: string;
}
export interface RadarMapping {
  labels: string;
  values: string;
}
export interface SankeyMapping {
  source: string;
  target: string;
  value: string;
}
export interface FunnelMapping {
  stage: string;
  value: string;
}
export interface GaugeMapping {
  value: string;
  min?: string;
  max?: string;
}

/**
 * Union of mapping shapes accepted by the `Chart` ComponentNode (analytical charts only).
 * Mermaid/Kanban/DataTable have their own ComponentNode types with embedded mapping shapes
 * in `packages/catalog/src/component-catalog.ts`.
 */
export type ChartMapping =
  | BarMapping
  | LineMapping
  | AreaMapping
  | PieMapping
  | ScatterMapping
  | HeatmapMapping
  | HistogramMapping
  | RadarMapping
  | SankeyMapping
  | FunnelMapping
  | GaugeMapping;

/** Narrowing helper: given a chart type literal, returns the matching mapping interface. */
export type MappingFor<T extends ChartType> = {
  bar: BarMapping;
  line: LineMapping;
  area: AreaMapping;
  pie: PieMapping;
  scatter: ScatterMapping;
  heatmap: HeatmapMapping;
  histogram: HistogramMapping;
  radar: RadarMapping;
  sankey: SankeyMapping;
  funnel: FunnelMapping;
  gauge: GaugeMapping;
}[T];
